// eslint-disable-next-line no-unused-vars
import React, { useState, useMemo } from 'react';
import {
  bool,
  string,
  arrayOf,
  object,
  func,
  number,
  oneOfType,
  shape,
} from 'prop-types';
import { Button, Checkbox, Dropdown, Loading, TextInput } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { uniqueId } from 'lodash';
import { useTheme } from '@emotion/react';
import { Info, TriangleAlert, Check, Plus, Wallet } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import useWheelHack from 'Hooks/useWheelHack';
import OrderProducts from 'Containers/Order/OrderProducts';
import OrderInfo from 'Containers/Order/OrderInfo';
import OrderSummary from 'Containers/Order/OrderSummary';
import OrderQuickAdd from 'Containers/Order/OrderQuickAdd';
import { NWNotification } from 'Components/shared';
import { COUNTRIES } from 'Constants';

import PromptModal from 'Containers/PromptModal/PromptModal';
import CsvIcon from 'Assets/images/csv.svg';

const StyledCheckoutPage = styled.form`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 20px;
  margin-top: 25px;

  .right {
    width: 310px;
  }

  > .main {
    .header {
      margin-bottom: 10px;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .headerRight {
        display: flex;
        align-items: center;

        > div:nth-of-type(2) {
          padding-left: 24px;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            width: 1px;
            height: 24px;
            background: #e2e2e2;
          }
        }

        .dropShipment {
          display: flex;
          align-items: center;
          gap: 5px;

          .react-tooltip.stockTooltip {
            max-width: 200px;
            font-size: 0.875rem;
            padding: 10px;
            background-color: ${ props => props.theme.colors.lightGray };
            color: ${ props => props.theme.colors.darkGray };
          }
        }
      }

      .currencyDropdown {
        margin-left: 32px;

        label {
          display: flex;
          align-items: center;
        }

        svg {
          height: 24px;
          min-width: 24px;
          margin-right: 8px;
        }

        .cds--list-box {
          background: transparent;
          border: 1px solid ${ props => props.theme.products.colors.border };;
          border-radius: 4px;
        }
      }

      .titleSection {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .cartTitle {
          display: flex;
          align-items: baseline;
          gap: 30px;
          font-size: 28px;
          font-weight: 600;

          .cartMeta {
            display: flex;
            gap: 12px;
            font-size: 0.875rem;
            font-weight: 500;
            color: ${ props => props.theme.colors.darkGray };

            .value {
              font-weight: 700;
            }
          }
        }


        .downloadCSV {
          display: none;
        }
      }

      .downloadCSV {
        display: flex;
        align-items: center;
        margin-left: 24px;

        .downloadBtn {
          color: #3a3a3a;
          border: none;
          background: transparent;

          &:hover,
          &:active,
          &:focus {
            background: transparent;
            outline: none;
            border: none;
            box-shadow: none;
          }
        }

        svg {
          margin-left: 24px;
          width: 27px;
        }
      }
    }

    .messages {
      display: flex;
      flex-direction: column;
      margin-bottom: 10px;

      .message {
        &.success {
          background-color: ${ props => props.theme.products.colors.quickViewButtonBg };
        }

        &.error {
          background-color: ${ props => props.theme.colors.lightRed };
        }

        svg {
          margin-right: 10px;
        }

        display: flex;
        align-items: center;
        width: 100%;
        margin: 5px 0;
        padding: 10px 20px;
        font-size: 0.875rem;
        font-weight: 500;
        color: ${ props => props.theme.colors.darkGray };

        p {
          color: inherit;
          font-size: inherit;
          line-height: inherit;
          font-weight: inherit;
        }
      }
    }

    .tools {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin: 15px -10px;

      > * {
        margin: 0 10px;
      }
    }
  }

  .transportValue {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 15px 30px;
    background-color: ${ props => props.theme.colors.shadowGray };
    font-size: 0.875rem;
    color: ${ props => props.theme.colors.darkGray };
    border-left: 1px solid ${ props => props.theme.products.colors.border };
    border-right: 1px solid ${ props => props.theme.products.colors.border };
    //todo: remove after adding quick add
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type=number] {
      -moz-appearance: textfield;
    }

    .cds--form-item {
      max-width: 83px;
      margin: 0 10px;

      input {
        padding: 0 10px;
        border: 1px solid ${ props => props.theme.products.colors.border };
        box-sizing: border-box;
        font-size: 1.125rem;
        color: ${ props => props.theme.colors.darkGray };
        text-align: right;
      }
    }

    span {
      font-size: 1.125rem;
      text-transform: capitalize;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    grid-template-columns: auto;

    > .main {
      .header {
        &.externalReseller {
          flex-direction: column;
          align-items: flex-start;

          .currencyDropdown {
            margin-left: 0;

            .cds--list-box__menu {
              min-width: auto;
            }
          }

          .headerRight {
            border-top: 1px solid #e2e2e2;
            padding-top: 15px;
            margin-top: 15px;

            > div:nth-of-type(2) {
              padding-left: 0;

              &::after {
                content: none;
              }
            }
          }

          .titleSection {
            width: 100%;

            .downloadCSV {
              display: block;

              span {
                display: none;
              }
            }
          }

          .downloadCSV {
            display: none;
          }
        }

        h1 {
          font-size: 24px;
          font-weight: 600;

          &.cartTitle {
            flex-direction: column;
            gap: 5px !important;
          }
        }

        .downloadCSV span {
          display: none;
        }

        .headerRight {
          > div:nth-of-type(2) {
            padding-left: 0;
          }
        }
      }
  
      .tools {
        flex-direction: column;
        gap: 10px;
        margin: 15px 0;

        > * {
          margin: 0 10px;
          min-width: 100%;
        }
      }
    }

    .right {
      width: auto;
    }

    .transportValue {
      padding: 15px;

      p {
        font-weight: 600;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  &:hover, &:active, &:focus {
    background-color: transparent !important;
    color: ${ props => props.theme.colors.darkGray };
  }
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  width: 100%;
  max-width: none !important;
  padding: 0 !important;
  margin: 0 !important;
  height: auto !important;
  min-height: 0 !important;
  background-color: transparent !important;
  border-top: ${ props => props.theme.canCreate ? `1px solid ${props.theme.colors.ligthGray}` : '' };
  color: ${ props => props.theme.colors.gray };

  svg {
    margin-right: 10px !important;
  }
`;

const StyledDropdown = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 25px 0;
  position: relative;

  .cds--loading {
    position: absolute;
    top: 30%;
    right: 175px;
    z-index: 5;
  }

  .cds--dropdown__wrapper {
    grid-gap: 0;
    border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
  }

  .wishlistsDropdown {
    width: 200px;
    height: 40px;

    .cds--dropdown {
      min-width: 100%;
    }

    .cds--list-box__field {
      background-color: ${ props => props.theme.products.colors.addToFavorite };
      outline: 0;
      border: 0;
      span {
        margin: 0 auto;
        font-size: 0.875rem;
        color: ${ props => props.theme.products.colors.addToFavoriteText };
      }

      .cds--list-box__menu-icon {
        right: 0;
        height: 100%;
        width: 35px;
        background-color: ${ props => props.theme.colors.white };
        border-left: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };

        svg {
          fill: ${ props => props.theme.products.colors.addToFavoriteText };
          transform: scale(1.25);
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .cds--dropdown__wrapper {
      width: 100%;
      height: 42px;
    }

    .wishlistsDropdown {
      width: 100%;
      height: 40px;

      .cds--list-box__field, .cds--list-box__menu {
        max-width: none;
      }
    }
  }
`;

const StyledEmptyCartButton = styled(Button)`
  height: 40px;
  max-width: 150px;
  width: 100%;
  line-height: 40px;
  font-size: 0.875rem;
  border: 1px solid ${ props => props.theme.colors.default } !important;
  color: ${ props => props.theme.colors.default } !important;

  .cds--loading {
    margin-left: 20px;
  }
`;

const Checkout = props => {
  const {
    order,
    orderType,
    products,
    formRef,
    messages,
    wishlists,
    canPlaceOrder,
    canReserve,
    onSubmit,
    transportValue,
    setTransportValue,
    onRemoveProduct,
    onUpdateProduct,
    onAddToWishlist,
    loadingSubmit,
    loadingWishlist,
    onEmptyCart,
    emptyCartDisabled,
    loadingEmptyCart,
    onCartDownload,
    selectedDSCountry,
    setSelectedDSCountry,
    canPlaceDropShipmentOrder,
    canPlaceEmagOrder,
    canPlaceInternalOrder,
    canPlaceExternalOrder,
    canPlaceStockReservationOrder,
    onCurrencyChange,
    isExternalReseller,
    currencyDropdownValues,
    selectedCurrencyItem,
    onModalOpen,
    onModalClose,
    isModalOpen,
    setIsFormValid,
    shouldShowErrors,
    unreservableProducts,
    unavailableProductIds,
    numberOfOrders,
    hasSupplierProducts,
    productsType,
    totalItems,
    orderLines,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const preventWheel = useWheelHack();
  const [selectedCurrency, setSelectedCurrency] = useState();

  return (
    <StyledCheckoutPage className="grid-container" ref={ formRef }>
      <div className="main">
        <div className={ `header ${ isExternalReseller ? 'externalReseller' : '' }` }>
          <div className="titleSection">
            <h1 className="cartTitle">
              {t('order.checkout')}

              {!!products?.length && (
                <span className="cartMeta">
                  <span>
                    {t('products.products')}: <span className="value">{orderLines}</span>
                  </span>
                  <span>
                    {t('products.quantity')}: <span className="value">{totalItems}</span>
                  </span>
                </span>
              )}
            </h1>
            <div className="downloadCSV">
              <Button
                kind="ghost"
                className="downloadBtn"
                onClick={ onCartDownload }
              >
                <span>{ t('order.downloadCart') }</span>
                <CsvIcon />
              </Button>
            </div>
          </div>
          <div className="headerRight">
            { isExternalReseller && (
              <>
                <PromptModal
                  modalOpen={ isModalOpen }
                  onCloseModal={ onModalClose }
                  onConfirm={ () => { onCurrencyChange(selectedCurrency.id); onModalClose(); } }
                  header={ <h4>{ t('order.changeCurrency') }</h4> }
                  body={ <p dangerouslySetInnerHTML={ { __html: t('order.currencyModalMessage', { currency: selectedCurrency?.label }) } } /> }
                />
                <div className="currencyDropdown">
                  <Dropdown
                    id="currencyChange"
                    titleText={(
                      <>
                        <Wallet />
                        <span>{t('order.currencySelect')}</span>
                      </>
                    )}
                    label={ t('order.dropdownLabel') }
                    type="inline"
                    items={ currencyDropdownValues }
                    onChange={ value => { setSelectedCurrency(value.selectedItem); onModalOpen(); } }
                    initialSelectedItem={ selectedCurrencyItem }
                    selectedItem={ selectedCurrencyItem }
                  />
                </div>
              </>
            )}
            { (canPlaceDropShipmentOrder && (
              canPlaceEmagOrder
              || canPlaceInternalOrder
              || canPlaceExternalOrder
            )) && (
              <div className="dropShipment">
                <Checkbox
                  id={ uniqueId('dropShipment') }
                  name="dropShipment"
                  labelText={ t('common.dropShipment') }
                  disabled={ hasSupplierProducts }
                  onChange={ (_, { checked }) => order.setIsDropShipping(checked) }
                  checked={ !!order.dropShipment }
                  data-test="dropShipment"
                />
                { hasSupplierProducts && (
                  <>
                    <span id="dropshipDisabled"><Info size={ 18 } /></span>
                    <ReactTooltip
                      className="stockTooltip"
                      anchorId="dropshipDisabled"
                      variant="light"
                      offset={ 10 }
                      place="bottom"
                      content={ t('product.dropShipmentWarning') }
                    />
                  </>
                ) }
              </div>
            ) }
            <div className="downloadCSV">
              <Button
                kind="ghost"
                className="downloadBtn"
                onClick={ onCartDownload }
              >
                <span>{ t('order.downloadCart') }</span>
                <CsvIcon />
              </Button>
            </div>
          </div>
        </div>
        { (numberOfOrders > 1) && <NWNotification errors={ [{ type: 'info', message: t('orders.multipleSuppliersWarning', { numberOfOrders }) }] } /> }
        <div className="messages">
          { messages.map(message => (
            <div
              key={ uniqueId('order_message_') }
              className={ `message ${ message.type }` }
            >
              { message.type === 'error' ? <TriangleAlert /> : <Check /> }
              <p
                key={ uniqueId() }
                dangerouslySetInnerHTML={{
                  __html: message.msg
                }}
              />
            </div>
          )) }
        </div>
        <OrderProducts
          order={ order }
          products={ products }
          editable
          onUpdateProduct={ onUpdateProduct }
          onRemoveProduct={ onRemoveProduct }
          shouldPromptChanges={ false }
          selectedDSCountry={ selectedDSCountry }
          unreservableProducts={ unreservableProducts }
          unavailableProductIds={ unavailableProductIds }
        />
        { order.dropShipment && (
          <div className="transportValue">
            <p>{ t('order.extraTax') }: </p>
            <TextInput
              tabIndex={ 0 }
              value={ transportValue }
              id={ uniqueId('transportValue') }
              type="number"
              labelText=""
              onChange={ setTransportValue }
              light
              min={ 0 }
              step="any"
              ref={ preventWheel }
            />
            <span>{ COUNTRIES[selectedDSCountry]?.symbol }</span>
          </div>
        ) }
        <OrderQuickAdd />
        <div className="tools">
          <StyledEmptyCartButton
            className="emptyCart red"
            disabled={ loadingEmptyCart || emptyCartDisabled }
            onClick={ onEmptyCart }
            kind="ghost"
          >
            { loadingEmptyCart && <Loading active small withOverlay={ false } /> }
            { t('cart.emptyCart') }
          </StyledEmptyCartButton>
          <StyledDropdown>
            { loadingWishlist && <Loading small withOverlay={ false } active /> }
            <Dropdown
              disabled={ products?.length === 0 || loadingWishlist }
              className="wishlistsDropdown"
              id={ uniqueId('wishlists') }
              label={ t('cart.saveToWishlist') }
              type="inline"
              onChange={ onAddToWishlist }
              items={ wishlists }
              selectedItem={ null }
              itemToElement={ item => {
                if (item.createButton) {
                  return (
                    <StyledButton
                      theme={{ ...theme, canCreate: true }}
                      kind="ghost"
                      onClick={ item.onClick }
                      style={{ fontWeight: 700 }}
                    >
                      <Plus />
                      { t('wishlists.newList') }
                    </StyledButton>
                  );
                }
                return item.name || item.label || item.text;
              } }
            />
          </StyledDropdown>
        </div>
        <OrderInfo
          order={ order }
          selectedDSCountry={ selectedDSCountry }
          setSelectedDSCountry={ setSelectedDSCountry }
          setIsFormValid={ setIsFormValid }
          shouldShowErrors={ shouldShowErrors }
        />
      </div>
      <div className="right">
        <OrderSummary
          loading={ loadingSubmit }
          orderType={ orderType }
          order={ order }
          onPlaceOrder={ onSubmit }
          canPlaceOrder={ canPlaceOrder }
          canReserve={ canPlaceStockReservationOrder && canReserve && !isExternalReseller }
          canPlaceStockReservationOrder={ canPlaceStockReservationOrder && !isExternalReseller }
          displayVat={ !isExternalReseller }
          selectedDSCountry={ selectedDSCountry }
          hasSupplierProducts={ hasSupplierProducts }
          productsType={ productsType }
        />
      </div>
    </StyledCheckoutPage>
  );
};
Checkout.propTypes = {
  onEmptyCart: func,
  emptyCartDisabled: bool,
  loadingEmptyCart: bool,
  order: object,
  orderType: string,
  wishlists: arrayOf(object),
  formRef: object,
  products: arrayOf(object),
  messages: arrayOf(object),
  canPlaceOrder: bool,
  canReserve: bool,
  onAddToWishlist: func,
  onSubmit: func,
  transportValue: oneOfType([string, number]),
  setTransportValue: func,
  onRemoveProduct: func,
  onUpdateProduct: func,
  loadingSubmit: bool,
  loadingWishlist: bool,
  selectedDSCountry: number,
  setSelectedDSCountry: func,
  onCartDownload: func,
  canPlaceDropShipmentOrder: bool,
  canPlaceEmagOrder: bool,
  canPlaceInternalOrder: bool,
  canPlaceExternalOrder: bool,
  canPlaceStockReservationOrder: bool,
  onCurrencyChange: func.isRequired,
  isExternalReseller: bool.isRequired,
  selectedCurrencyItem: shape({
    id: string.isRequired,
    label: string.isRequired
  }),
  currencyDropdownValues: arrayOf(shape({
    id: string.isRequired,
    label: string.isRequired
  })),
  onModalOpen: func.isRequired,
  onModalClose: func.isRequired,
  isModalOpen: bool.isRequired,
  setIsFormValid: func,
  shouldShowErrors: bool,
  unreservableProducts: arrayOf(string),
  unavailableProductIds: arrayOf(string),
  numberOfOrders: number,
  hasSupplierProducts: bool,
  productsType: string,
  totalItems: number,
  orderLines: number,
};
Checkout.displayName = 'CheckoutComponent';

export default Checkout;
